import React, { useEffect } from 'react';
import classnames from 'classnames';
import { Bubble } from 'tea-component';
import GameImage from '@home/components/GameImage';
import SwiperBtn from '@intelligence/components/SwiperBtn';
import SearchSelect from '../SearchSelect';
import useSlideEffect from './_hooks/useSlideEffect';

import './index.scss';

import type { IBaseProps } from '@/pages/intelligence/types/common';
import type { ITrendingGame } from '../../types';

interface IGameSelectProps extends IBaseProps {
  // 布局刷新依赖项，触发宽度重新计算
  layoutRefreshDeps: any[];
  options: ITrendingGame[];
  value: string;
  onChange: (payload: ITrendingGame) => void;
}

const GameSelect: React.FC<IGameSelectProps> = (props) => {
  const { style, className, layoutRefreshDeps, options, value, onChange, selectedUidAndId } = props;

  const {
    // 容器dom
    containerRef,
    // 列表容器dom
    listContainerRef,
    // 是否超宽，导致溢出隐藏、滚动
    isOverflow,
    // 左右滑动按钮禁用状态
    slideDisabledState,
    // 点击左滑按钮的回调
    handleSlideLeft,
    // 点击右滑按钮的回调
    handleSlideRight,
  } = useSlideEffect({
    /**
     * REMARK: 这里10、10分别是左右滑动按钮的宽度、margin，100为All按钮的宽度、内边距之和，20是All按钮的margin-left
     */
    othersWidth: (10 + 10) * 2 + 100 + 20,
    // 重新计算宽度的依赖
    updateDeps: [layoutRefreshDeps, options],
  });

  const handleSlideIntoView = (index: number) => {
    const optionNode = listContainerRef.current?.children?.[index];
    if (!optionNode) return;
    optionNode.scrollIntoView({
      inline: 'center',
      behavior: 'smooth',
    });
  };

  const handleGameSelect = (payload: ITrendingGame) => {
    const optionIdx = options?.findIndex(
      (option) => option?.unified_id + option?.id === payload?.unified_id + payload?.id,
    );
    if (optionIdx !== -1) {
      const option = options?.[optionIdx];

      // handleSlideIntoView(optionIdx);
      onChange(option);
    }
  };

  useEffect(() => {
    if (selectedUidAndId && listContainerRef.current) {
      const optionIdx = options?.findIndex(
        (option) => option?.unified_id + option?.id === selectedUidAndId,
      );
      if (optionIdx !== -1) {
        handleSlideIntoView(optionIdx);
      }
    }
  }, [selectedUidAndId, listContainerRef.current]);

  return (
    <div
      ref={containerRef}
      style={style}
      className={classnames('dbi-swipeable-game-select', className)}
    >
      {isOverflow && (
        <SwiperBtn
          gradient
          className="dbi-swipeable-game-select__swipe-btn dbi-swipeable-game-select__swipe-btn--left"
          type="prev"
          disabled={slideDisabledState.prev}
          onClick={handleSlideLeft}
        />
      )}
      <div ref={listContainerRef} className="rainbow-avatar-swiper">
        {options?.map((option) => {
          // const isSelected = value === option?.unified_id;
          const isSelected = selectedUidAndId === option?.unified_id + option?.id;
          const entityType = option?.type;
          return (
            <div className="rainbow-avatar-swiper-item">
              <Bubble placement="top-start" content={`${option?.name}`} dark>
                <GameImage
                  hasImageLoading={true}
                  className={classnames({
                    'rainbow-avatar-img-chosen': isSelected,
                    'rainbow-avatar-img-unchosen': !isSelected,
                    [`rainbow-avatar-img-chosen-${entityType}`]: isSelected,
                    [`rainbow-avatar-img-unchosen-${entityType}`]: !isSelected,
                  })}
                  entityType={entityType}
                  src={option?.cover}
                  onClick={() => {
                    onChange(option);
                  }}
                />
              </Bubble>
            </div>
          );
        })}
      </div>
      {isOverflow && (
        <SwiperBtn
          gradient
          className="dbi-swipeable-game-select__swipe-btn dbi-swipeable-game-select__swipe-btn--right"
          type="next"
          disabled={slideDisabledState.next}
          onClick={handleSlideRight}
        />
      )}
      <SearchSelect
        className="dbi-swipeable-game-select__search-select"
        options={options}
        value={value}
        selectedUidAndId={selectedUidAndId}
        onSelect={handleGameSelect}
      />
    </div>
  );
};

export default GameSelect;
